import { useState, useEffect, useContext, useCallback } from 'react';
import { Paper, Box } from 'bonfire-kindling';
import { CancelToken } from 'axios';
import _forEach from 'lodash/forEach';

import axios from 'helpers/axios';
import { regionDataToLocations, locationsToRegionData } from 'helpers/locations';
import { pickVendorBusinessInfoFields } from 'helpers/vendor';
import { VENDORS_ME, VENDORS_ME_TAXONOMYCODES } from 'constants/apis';
import { Stepper, Page, ContentSwitch, Loader } from 'components';
import { ToastContext } from 'contexts';
import Welcome1 from './Welcome1/Welcome1';
import Welcome2 from './Welcome2/Welcome2';
import Welcome3 from './Welcome3/Welcome3';
import Welcome4 from './Welcome4/Welcome4';
import Welcome5 from './Welcome5/Welcome5';

const PAPER_MAX_WIDTH = '1000px';

const steps = {
  businessDescription: 0,
  businessInfo: 1,
  commodityCodes: 2,
  locations: 3,
  keywords: 4,
};

const getCompleteIndex = (
  {
    vendorContactOrganizationName,
    businessDescription,
    numEmployees,
    role,
    taxonomyCodes,
    selectedLocations,
  },
  hasVisitedKeywords
) => {
  const isStep1Done = businessDescription !== '' && vendorContactOrganizationName !== '';
  const isStep2Done = numEmployees !== '' && role !== '';
  const isStep3Done = taxonomyCodes && taxonomyCodes.length > 0;
  const isStep4Done = selectedLocations && selectedLocations.length > 0;
  const isStep5Done = hasVisitedKeywords; // Keywords are optional.

  if (!isStep1Done) return null;
  if (!isStep2Done) return steps.businessDescription;
  if (!isStep3Done) return steps.businessInfo;
  if (!isStep4Done) return steps.commodityCodes;
  if (!isStep5Done) return steps.locations;

  return steps.keywords;
};

const sources = {};

const Welcome = ({ onOnboardingComplete }) => {
  const toastContext = useContext(ToastContext);
  const [index, setIndex] = useState(steps.businessDescription);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [hasVisitedKeywords, setHasVisitedKeywords] = useState(false);
  const [vendorData, setVendorData] = useState({});
  const [completeIndex, setCompleteIndex] = useState(null);

  // Fetch the data we need to initialize the component.
  useEffect(() => {
    const fetchVendorData = async () => {
      if (sources.vendorData) {
        sources.vendorData.cancel();
      }

      const source = CancelToken.source();
      sources.vendorData = source;
      const options = { cancelToken: source.token };

      try {
        const [{ data: vendor }, { data: taxonomyCodes }] = await Promise.all([
          axios.get(VENDORS_ME, options),
          axios.get(VENDORS_ME_TAXONOMYCODES),
        ]);

        const mappedVendorData = {
          ...pickVendorBusinessInfoFields(vendor),
          taxonomyCodes: taxonomyCodes,
          selectedLocations: regionDataToLocations(vendor.regionData),
          keywords: vendor.keywords,
        };
        setVendorData(mappedVendorData);
        const newCompleteIndex = getCompleteIndex(mappedVendorData, hasVisitedKeywords);
        setCompleteIndex(newCompleteIndex);
        if (newCompleteIndex !== null) {
          setIndex(Math.min(newCompleteIndex + 1, steps.keywords));
        }
      } catch (err) {
        toastContext.triggerToast({});
      }

      setIsDataLoaded(true);
    };

    fetchVendorData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCompleteIndex(getCompleteIndex(vendorData, hasVisitedKeywords));
  }, [vendorData, hasVisitedKeywords]);

  // Clean up the sources when the component unmounts.
  useEffect(() => {
    return () => {
      _forEach(sources, source => {
        source.cancel();
      });
    };
  }, []);

  const incrementIndex = () => setIndex(prevIndex => Math.min(prevIndex + 1, steps.keywords));
  const decrementIndex = () =>
    setIndex(prevIndex => Math.max(prevIndex - 1, steps.businessDescription));

  const onSubmit = useCallback(data => {
    setVendorData(prevState => ({ ...prevState, ...data }));
    incrementIndex();
  }, []);
  const onChange = useCallback(data => {
    setVendorData(prevState => ({ ...prevState, ...data }));
  }, []);

  const completeOnboarding = useCallback(async () => {
    const vendorPayload = {
      vendorContactOrganizationName: vendorData.vendorContactOrganizationName,
      businessDescription: vendorData.businessDescription,
      role: vendorData.role,
      numEmployees: vendorData.numEmployees,
      regionData: locationsToRegionData(vendorData.selectedLocations),
      isOnboarded: true,
    };
    const taxonomyCodesPayload = vendorData.taxonomyCodes.map(code => code.taxonomyCodeID);
    try {
      await axios.patch(VENDORS_ME, vendorPayload);
      await axios.put(VENDORS_ME_TAXONOMYCODES, taxonomyCodesPayload);
      onOnboardingComplete();
    } catch {
      toastContext.triggerToast({});
    }
  }, [vendorData, toastContext, onOnboardingComplete]);

  return (
    <Page>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          height: '100%',
          paddingTop: '30px',
        }}
      >
        {isDataLoaded ? (
          <>
            <Stepper
              steps={[{}, {}, { cy: 'commodityCodes' }, {}, {}]}
              activeIndex={index}
              completeIndex={completeIndex}
              onClick={newIndex => setIndex(newIndex)}
            />
            <Paper
              sx={{
                pt: 6,
                pb: 7,
                px: 4,
                mt: 3,
                maxWidth:
                  index === steps.commodityCodes || index === steps.keywords
                    ? PAPER_MAX_WIDTH
                    : '600px',
              }}
              elevation={0}
            >
              <Box mb={2}>
                <ContentSwitch index={index}>
                  <Welcome1 values={vendorData} onSubmit={onSubmit} onChange={onChange} />
                  <Welcome2
                    values={vendorData}
                    onSubmit={onSubmit}
                    onChange={onChange}
                    decrementIndex={decrementIndex}
                  />
                  <Welcome3
                    values={vendorData}
                    onSubmit={onSubmit}
                    onChange={onChange}
                    decrementIndex={decrementIndex}
                  />
                  <Welcome4
                    values={vendorData}
                    onSubmit={onSubmit}
                    onChange={onChange}
                    decrementIndex={decrementIndex}
                  />
                  <Welcome5
                    values={vendorData}
                    onSubmit={onSubmit}
                    decrementIndex={decrementIndex}
                    completeOnboarding={completeOnboarding}
                    setHasVisitedKeywords={setHasVisitedKeywords}
                  />
                </ContentSwitch>
              </Box>
            </Paper>
          </>
        ) : (
          <Loader variant="puff" isCentered />
        )}
      </Box>
    </Page>
  );
};

export default Welcome;
